/**
 * PATH: packages/email-hub/src/feature/pages/SyncSettingsPage.tsx
 * FILE_ID: EMAILHUB-FEATURE-PAGES-SYNCSETTINGSPAGE-001
 * PURPOSE: Canonical body of this email pages module. Lifted
 *          from apps/<tenant>/src/features/email/pages/SyncSettingsPage.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/pages/SyncSettingsPage.tsx: thin re-export
 *     shim that points back here.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Loader2,
  RotateCcw,
  History,
  Clock,
  Mail,
  Activity,
  Bug,
  Info,
  XCircle,
  Zap,
} from 'lucide-react'
import { emailApi } from '../lib/api'
import { cn } from '../../_internal/lib/utils'
import { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useToast } from '../../_internal/components/Toast'
import {
  useOutlookStatus,
  useGmailStatus,
  useSyncEmails,
  useEmailStats,
} from '../hooks/useEmails'
import {
  useBackfillStateView,
  BACKFILL_MAX_DAYS,
  BACKFILL_STEPS,
  BACKFILL_COMPLETE_AFTER_EMPTY,
} from '../hooks/useEmailBackfill'
import { useIntegrationEvents } from '../../_internal/services/integrationEvents'
import OrgMailboxHealthPanel from '../components/OrgMailboxHealthPanel'

function formatRelative(ms: number | null | undefined): string {
  if (!ms) return 'never'
  const s = Math.floor((Date.now() - ms) / 1000)
  if (s < 30) return 'just now'
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function severityClass(s: string): string {
  if (s === 'error') return 'text-red-500'
  if (s === 'warning') return 'text-amber-500'
  return 'text-muted-foreground'
}

// ----------------------------------------------------------------------------
// Sync diagnostics — fetches the Quotech-backend's authoritative view of the
// email sync pipeline (token state, last 5 sync logs, latest local email,
// active sync, recommendations). This is the panel a user can inspect to
// answer "why isn't my mail syncing?" without an engineer needing SSH access.
// ----------------------------------------------------------------------------

interface DiagnosticsOutlook {
  connected: boolean
  user_email?: string | null
  tenant_id?: string | null
  token_expires_at?: string | null
  is_expired?: boolean | null
  has_refresh_token?: boolean
  scopes?: string[]
  created_at?: string | null
  updated_at?: string | null
}

interface DiagnosticsLog {
  id: number
  status: string
  started_at: string | null
  finished_at: string | null
  emails_found: number | null
  emails_synced: number | null
  emails_ignored: number | null
  error_message: string | null
  duration_seconds: number | null
}

interface DiagnosticsLatestEmail {
  id: string
  subject: string | null
  received_at: string | null
  age_hours: number | null
  sender_name: string | null
  sender_email: string | null
}

interface DiagnosticsActiveSync {
  id: number
  started_at: string | null
  age_seconds: number | null
}

interface DiagnosticsGraphMessage {
  graph_id: string
  subject: string
  from_address: string | null
  from_name: string | null
  received_at: string | null
  parent_folder_id: string | null
  is_read: boolean | null
  folder_name: string | null
}

interface DiagnosticsInboxTreeEntry {
  id: string | null
  name: string | null
  depth: number
  total: number | null
  unread: number | null
  newest_received_at: string | null
}

interface DiagnosticsGraphProbe {
  ok: boolean
  error?: string
  latest?: DiagnosticsGraphMessage | null
  inbox_recent?: DiagnosticsGraphMessage[]
  all_folders_recent?: DiagnosticsGraphMessage[]
  folders?: Record<string, { name: string | null; total: number; unread: number; child_folder_count?: number }>
  inbox_tree?: DiagnosticsInboxTreeEntry[]
  inbox_tree_newest_received_at?: string | null
  sync_endpoint?: string
  sync_max_emails_per_call?: number
}

interface DiagnosticsResponse {
  now: string
  organisation_id: number
  user_id: number
  outlook: DiagnosticsOutlook
  latest_email: DiagnosticsLatestEmail | null
  total_emails: number
  sync_logs: DiagnosticsLog[]
  active_sync: DiagnosticsActiveSync | null
  graph_probe?: DiagnosticsGraphProbe | null
  recommendations: string[]
}

function useDiagnostics(pollMs: number = 30_000) {
  const [data, setData] = useState<DiagnosticsResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [probing, setProbing] = useState(false)

  const fetchOnce = useCallback(async (opts?: { probe?: boolean }) => {
    if (opts?.probe) setProbing(true)
    else setLoading(true)
    setError(null)
    try {
      // ?probe=graph asks the backend to round-trip Microsoft Graph and
      // return what the connected mailbox actually contains right now.
      // Slow (1-3s) so we keep it opt-in via a button rather than the
      // 30s background poll.
      const url = opts?.probe
        ? '/api/emails/sync/diagnostics/?probe=graph'
        : '/api/emails/sync/diagnostics/'
      const result = await emailApi<DiagnosticsResponse>(url)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load diagnostics')
    } finally {
      setLoading(false)
      setProbing(false)
    }
  }, [])

  useEffect(() => { void fetchOnce() }, [fetchOnce])

  // Visibility-aware polling so we stay fresh while the page is open
  // but don't burn quota when the user moves away.
  const fetchRef = useRef(fetchOnce)
  fetchRef.current = fetchOnce
  useEffect(() => {
    if (!pollMs) return
    let id: number | null = null
    const start = () => {
      if (id !== null) return
      id = window.setInterval(() => {
        if (!document.hidden) void fetchRef.current()
      }, pollMs)
    }
    const stop = () => {
      if (id !== null) { window.clearInterval(id); id = null }
    }
    let lastHiddenAt = 0
    const onVis = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now()
        stop()
      } else {
        void fetchRef.current()
        start()
      }
    }
    // BUG-0026: gate focus events on a recent hidden transition so
    // right-click noise doesn't queue a sync-status refetch.
    const onFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt < 30_000) {
        void fetchRef.current()
        start()
      }
    }
    if (!document.hidden) start()
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onFocus)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onFocus)
    }
  }, [pollMs])

  return {
    data,
    loading,
    probing,
    error,
    refresh: () => fetchOnce(),
    probe: () => fetchOnce({ probe: true }),
  }
}

/**
 * The body of the sync settings UI without any page chrome.
 *
 * Exported so the universal Settings modal can render it as a tab —
 * the user explicitly asked for sync settings to live "in the universal
 * setting not a separate page loader". Both the route at
 * `/email/settings` and the modal's `email-sync` tab render this same
 * component, so there's exactly one source of truth for sync UX.
 */
export function SyncSettingsContent() {
  const { session } = useApiAuth()
  const { stats } = useEmailStats()
  const outlook = useOutlookStatus()
  const gmail = useGmailStatus()
  const { sync, isSyncing } = useSyncEmails()
  const { toast } = useToast()

  const orgId = session?.organisation?.id ?? null
  const userId = session?.user?.id ?? null
  const backfill = useBackfillStateView(orgId, userId)

  const { events: backfillEvents, loading: eventsLoading, refresh: refreshEvents } =
    useIntegrationEvents({ sourcePrefix: 'client.email.sync', limit: 25, pollMs: 30_000 })

  const diagnostics = useDiagnostics(30_000)

  const handleForceFullResync = async () => {
    try {
      await sync(30, { forceFull: true })
      toast('Force full resync triggered - refreshing in a few seconds.', 'info')
      window.setTimeout(() => { void diagnostics.refresh() }, 8_000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Force resync failed'
      toast(msg, 'error')
    }
  }

  const recentBackfill = backfillEvents.filter(e =>
    typeof e.reason === 'string' && e.reason.startsWith('backfill_'),
  )

  const handleManualSync = async (daysBack: number) => {
    try {
      // Always force_full=true on explicit window clicks: the user is
      // saying "actually go fetch this window from Outlook now". Without
      // forceFull the backend uses the delta cursor and returns 0 even
      // though days_back was 180 — exactly the symptom that gave us
      // "last 3 syncs returned 0 emails" while the mailbox is fine.
      // forceFull also lifts the client-side 30-day clamp.
      await sync(daysBack, { forceFull: true })
      toast(`Pulling last ${daysBack} days from your mailboxes - appearing shortly.`, 'info')
      window.setTimeout(() => { void diagnostics.refresh() }, 8_000)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed'
      toast(msg, 'error')
    }
  }

  const handleRestartBackfill = () => {
    backfill.reset()
    toast('Backfill cursor reset - history will be re-walked from now.', 'info')
  }

  const outlookOk = outlook.connected === true && !outlook.isExpired
  const gmailOk = gmail.connected === true && !gmail.isExpired
  // "Sync Now" / manual windows pull EVERY connected mailbox, so they're
  // usable as long as at least one provider is connected (the panel used
  // to gate purely on Outlook — "Sync & Status is Outlook-only").
  const anyMailboxOk = outlookOk || gmailOk

  return (
    <div className="space-y-6">
        {/* Outlook connection */}
        <section className="rounded-lg border border-border bg-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" /> Outlook connection
            </h2>
            {outlookOk ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              </span>
            ) : outlook.isExpired ? (
              <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Session expired
              </span>
            ) : outlook.connected === false ? (
              <span className="text-xs text-amber-600 dark:text-amber-400">Not connected</span>
            ) : (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
              </span>
            )}
          </div>
          {outlook.email && (
            <p className="text-xs text-muted-foreground">
              Mailbox: <span className="font-mono text-foreground">{outlook.email}</span>
            </p>
          )}
          {outlook.message && !outlookOk && (
            <p className="text-xs text-amber-500">{outlook.message}</p>
          )}
          {!outlookOk && (
            <button
              type="button"
              onClick={() => outlook.authorize()}
              className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1.5"
            >
              <ExternalLink className="w-3 h-3" />
              {outlook.isExpired ? 'Reconnect Outlook' : 'Connect Outlook'}
            </button>
          )}
        </section>

        {/* Gmail connection — parallel to Outlook so Google mailboxes
            get the same status + connect/reconnect affordance (the panel
            was previously Outlook-only). */}
        <section className="rounded-lg border border-border bg-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium flex items-center gap-2">
              <Mail className="w-4 h-4" /> Gmail connection
            </h2>
            {gmailOk ? (
              <span className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              </span>
            ) : gmail.isExpired ? (
              <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Session expired
              </span>
            ) : gmail.connected === false ? (
              <span className="text-xs text-amber-600 dark:text-amber-400">Not connected</span>
            ) : (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
              </span>
            )}
          </div>
          {gmail.email && (
            <p className="text-xs text-muted-foreground">
              Mailbox: <span className="font-mono text-foreground">{gmail.email}</span>
            </p>
          )}
          {!gmailOk && (
            <button
              type="button"
              onClick={() => gmail.authorize()}
              className="text-xs px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1.5"
            >
              <ExternalLink className="w-3 h-3" />
              {gmail.isExpired ? 'Reconnect Gmail' : 'Connect Gmail'}
            </button>
          )}
        </section>

        {/* Manual sync windows */}
        <section className="rounded-lg border border-border bg-card p-5 space-y-3">
          <div>
            <h2 className="text-sm font-medium flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Manual sync
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Auto-sync runs every 5 minutes while this tab is open and pulls
              new mail incrementally. The historical backfill below is also
              continuously walking older mail in the background. Click a
              window here to force a fresh fetch of that range from every
              connected mailbox (Outlook and Gmail) right now (bypasses the
              incremental cursor - useful when "Sync Now" returns 0 but you
              know there's new mail). Larger windows are slower and use more
              API quota.
            </p>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
            {[
              { days: 7,    label: '7 d' },
              { days: 30,   label: '30 d' },
              { days: 90,   label: '90 d' },
              { days: 180,  label: '180 d' },
              { days: 365,  label: '1 y' },
              { days: 730,  label: '2 y' },
              { days: 1825, label: '5 y' },
            ].map(({ days, label }) => (
              <button
                key={days}
                type="button"
                onClick={() => { void handleManualSync(days) }}
                disabled={!anyMailboxOk || isSyncing}
                title={`Force-fetch the last ${days} days from every connected mailbox now`}
                className={cn(
                  'text-xs px-2 py-2 rounded-md border border-border transition-colors',
                  'hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed',
                  'flex items-center justify-center gap-1.5',
                )}
              >
                {isSyncing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {label}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground pt-1">
            <span>Total local: <span className="font-mono text-foreground">{stats?.total ?? 0}</span></span>
            <span>Unread: <span className="font-mono text-foreground">{stats?.unread ?? 0}</span></span>
          </div>
        </section>

        {/* Server-side diagnostics — the authoritative view of what the
            backend thinks about the sync pipeline. Surfaces token state,
            last 5 sync runs, the latest email we actually have, and any
            recommendations the server can compute (e.g. "your inbox is
            12d old while Outlook is connected - click Force resync"). */}
        <section className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-medium flex items-center gap-2">
                <Bug className="w-4 h-4" /> Sync diagnostics
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Live snapshot of the server's view of your mailbox.
                Useful when "Sync Now" returns 0 emails but you know there
                are new messages - the panel below tells you exactly which
                Outlook account is connected and what the worker is doing.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { void diagnostics.refresh() }}
              disabled={diagnostics.loading}
              className="text-xs p-1.5 rounded-md border border-border hover:bg-accent transition-colors disabled:opacity-50"
              aria-label="Refresh diagnostics"
              title="Refresh diagnostics"
            >
              {diagnostics.loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </button>
          </div>

          {diagnostics.error && (
            <div className="text-xs text-red-500 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" />
              Couldn't load diagnostics: {diagnostics.error}
            </div>
          )}

          {diagnostics.data && (
            <>
              {/* Recommendations — server-computed verdicts. If empty,
                  the pipeline looks healthy and we say so explicitly. */}
              {diagnostics.data.recommendations.length === 0 ? (
                <div className="text-xs flex items-center gap-1.5 px-3 py-2 rounded-md bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Pipeline looks healthy. No issues detected.
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {diagnostics.data.recommendations.map((rec, i) => (
                    <li
                      key={i}
                      className="text-xs flex items-start gap-1.5 px-3 py-2 rounded-md bg-amber-500/10 text-amber-800 dark:text-amber-200 border border-amber-500/30"
                    >
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                      <span>{rec}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Connected mailbox details — answers "is the right MS
                  account connected?". The user_email from Microsoft
                  Graph trumps whatever they remember signing in with. */}
              <div className="text-xs space-y-1.5 border-t border-border pt-3">
                <p className="font-medium text-foreground">Connected mailbox</p>
                {diagnostics.data.outlook.connected ? (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                    <div>
                      <dt className="text-muted-foreground">Mailbox</dt>
                      <dd className="font-mono text-foreground break-all">
                        {diagnostics.data.outlook.user_email || '(unknown)'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Token expires</dt>
                      <dd className="font-mono">
                        {formatTimestamp(diagnostics.data.outlook.token_expires_at ?? null)}
                        {diagnostics.data.outlook.is_expired && (
                          <span className="ml-1.5 text-red-500">(expired)</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Refresh token</dt>
                      <dd className="font-mono">
                        {diagnostics.data.outlook.has_refresh_token ? 'present' : 'missing'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Tenant</dt>
                      <dd className="font-mono break-all">
                        {diagnostics.data.outlook.tenant_id || '—'}
                      </dd>
                    </div>
                  </dl>
                ) : (
                  <p className="text-muted-foreground">No Outlook token on record.</p>
                )}
              </div>

              {/* Latest email anchor — single most useful row. If this
                  is days behind today, sync is broken. */}
              {diagnostics.data.latest_email ? (
                <div className="text-xs space-y-1.5 border-t border-border pt-3">
                  <p className="font-medium text-foreground">Most recent email in our cache</p>
                  <dl className="space-y-1">
                    <div>
                      <dt className="text-muted-foreground inline">From: </dt>
                      <dd className="inline font-mono">
                        {diagnostics.data.latest_email.sender_name || ''}
                        {diagnostics.data.latest_email.sender_email
                          ? ` <${diagnostics.data.latest_email.sender_email}>`
                          : ''}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground inline">Subject: </dt>
                      <dd className="inline">
                        {diagnostics.data.latest_email.subject || '(no subject)'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground inline">Received: </dt>
                      <dd className="inline font-mono">
                        {formatTimestamp(diagnostics.data.latest_email.received_at)}
                        {diagnostics.data.latest_email.age_hours !== null && (
                          <span className={cn(
                            'ml-1.5',
                            diagnostics.data.latest_email.age_hours > 24
                              ? 'text-amber-500'
                              : 'text-muted-foreground',
                          )}>
                            ({Math.round(diagnostics.data.latest_email.age_hours)}h ago)
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground inline">Total in cache: </dt>
                      <dd className="inline font-mono">{diagnostics.data.total_emails}</dd>
                    </div>
                  </dl>
                  {diagnostics.data.latest_email.age_hours !== null
                    && diagnostics.data.latest_email.age_hours > 24
                    && diagnostics.data.outlook.connected
                    && !diagnostics.data.outlook.is_expired && (
                    <button
                      type="button"
                      onClick={() => { void handleForceFullResync() }}
                      disabled={isSyncing}
                      className="mt-2 text-xs px-3 py-1.5 rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-colors flex items-center gap-1.5 disabled:opacity-50"
                      title="Bypass any cached Graph state and pull fresh from Microsoft"
                    >
                      <Zap className="w-3 h-3" />
                      Force full resync now
                    </button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground border-t border-border pt-3">
                  No emails in the local cache yet.
                </p>
              )}

              {/* Active sync indicator. */}
              {diagnostics.data.active_sync && (
                <div className="text-xs space-y-1 border-t border-border pt-3">
                  <p className="font-medium text-foreground flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 animate-pulse text-emerald-500" />
                    Sync currently running
                  </p>
                  <p className="text-muted-foreground">
                    Started {formatTimestamp(diagnostics.data.active_sync.started_at)}  - {' '}
                    {Math.round(diagnostics.data.active_sync.age_seconds ?? 0)}s ago.
                  </p>
                </div>
              )}

              {/* Last 5 EmailSyncLog rows — the truth about whether the
                  Celery worker is doing anything. emails_found=0 with
                  status=success and no error_message means Microsoft
                  Graph itself replied empty. */}
              <div className="text-xs space-y-2 border-t border-border pt-3">
                <p className="font-medium text-foreground">Last 5 sync runs</p>
                {diagnostics.data.sync_logs.length === 0 ? (
                  <p className="text-muted-foreground">No syncs recorded yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {diagnostics.data.sync_logs.map(log => {
                      const isErr = log.status === 'error'
                      const isWarn = log.status === 'success' && (log.emails_found ?? 0) === 0
                      return (
                        <li
                          key={log.id}
                          className={cn(
                            'border rounded-md px-2.5 py-2 space-y-0.5',
                            isErr && 'border-red-500/50 bg-red-500/10 text-red-800 dark:text-red-200',
                            isWarn && 'border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-200',
                            !isErr && !isWarn && 'border-border',
                          )}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            {isErr ? (
                              <XCircle className="w-3 h-3" />
                            ) : isWarn ? (
                              <AlertTriangle className="w-3 h-3" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                            )}
                            <span className="font-mono uppercase">{log.status}</span>
                            <span className="ml-auto text-[10px] opacity-70 font-mono">
                              {formatTimestamp(log.started_at)}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] font-mono opacity-90">
                            <span>found: {log.emails_found ?? 0}</span>
                            <span>synced: {log.emails_synced ?? 0}</span>
                            <span>ignored: {log.emails_ignored ?? 0}</span>
                            {log.duration_seconds !== null && (
                              <span>{Math.round(log.duration_seconds * 10) / 10}s</span>
                            )}
                          </div>
                          {log.error_message && (
                            <p className="text-[11px] mt-1 break-words">
                              {log.error_message}
                            </p>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>

              {/* Live Graph probe — calls Microsoft Graph right now and
                  shows what the connected mailbox actually contains.
                  This is the first-principles diagnostic: if the local
                  cache stops at 10 Apr but Graph reports a 26 Apr
                  message in the Inbox, the sync pipeline is broken.
                  If Graph also stops at 10 Apr, the connected MS
                  account isn't receiving new mail (wrong account, mail
                  rule routing elsewhere, etc.). If new mail appears in
                  "All folders" but not "Inbox", an Outlook rule is
                  routing post-arrival and we don't pull from that
                  folder. */}
              <div className="text-xs space-y-2 border-t border-border pt-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-medium text-foreground">Live mailbox probe (Microsoft Graph)</p>
                  <button
                    type="button"
                    onClick={() => { void diagnostics.probe() }}
                    disabled={diagnostics.probing || !diagnostics.data?.outlook.connected}
                    className="text-[11px] px-2.5 py-1 rounded-md border border-border hover:bg-accent transition-colors flex items-center gap-1.5 disabled:opacity-50"
                    title="Round-trip Microsoft Graph to compare what's actually in the mailbox vs what's in our cache"
                  >
                    {diagnostics.probing ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Zap className="w-3 h-3" />
                    )}
                    {diagnostics.probing ? 'Probing…' : 'Run live mailbox check'}
                  </button>
                </div>
                {!diagnostics.data?.graph_probe ? (
                  <p className="text-muted-foreground">
                    Click "Run live mailbox check" to round-trip Microsoft Graph
                    and compare what's actually in the mailbox vs your local
                    cache (~2s). Use this when "Sync Now" returns 0 emails but
                    you know mail is arriving.
                  </p>
                ) : !diagnostics.data.graph_probe.ok ? (
                  <p className="text-red-500 break-words">
                    Probe failed: {diagnostics.data.graph_probe.error}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {/* Inbox newest from Graph */}
                    <div>
                      <p className="text-muted-foreground mb-1">
                        Newest in <span className="font-mono">{diagnostics.data.graph_probe.sync_endpoint}</span>:
                      </p>
                      {diagnostics.data.graph_probe.inbox_recent && diagnostics.data.graph_probe.inbox_recent.length > 0 ? (
                        <ul className="space-y-1">
                          {diagnostics.data.graph_probe.inbox_recent.slice(0, 5).map(m => (
                            <li key={m.graph_id} className="border border-border rounded px-2 py-1.5">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="font-mono text-[11px] truncate" title={m.subject}>
                                  {m.subject || '(no subject)'}
                                </span>
                                <span className="text-[10px] font-mono opacity-70">
                                  {formatTimestamp(m.received_at)}
                                </span>
                              </div>
                              <div className="text-[11px] text-muted-foreground truncate">
                                {m.from_name || ''} {m.from_address ? `<${m.from_address}>` : ''}
                                {m.folder_name && (
                                  <span className="ml-1.5">· folder: <span className="font-mono">{m.folder_name}</span></span>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-amber-500">
                          Microsoft Graph reports zero messages in Inbox.
                          Either no mail has arrived or the connected MS
                          account isn't the one you expect (mailbox:{' '}
                          <span className="font-mono">{diagnostics.data.outlook.user_email || 'unknown'}</span>).
                        </p>
                      )}
                    </div>

                    {/* Cross-folder check — mail routed away from Inbox */}
                    {diagnostics.data.graph_probe.all_folders_recent && diagnostics.data.graph_probe.all_folders_recent.length > 0 && (
                      (() => {
                        const inboxIds = new Set((diagnostics.data!.graph_probe!.inbox_recent ?? []).map(m => m.graph_id))
                        const outsideInbox = (diagnostics.data!.graph_probe!.all_folders_recent ?? [])
                          .filter(m => !inboxIds.has(m.graph_id))
                          .slice(0, 5)
                        if (outsideInbox.length === 0) return null
                        return (
                          <div>
                            <p className="text-muted-foreground mb-1">
                              Recent mail routed <span className="font-medium text-amber-500">outside Inbox</span> (rules / Junk / custom folders):
                            </p>
                            <ul className="space-y-1">
                              {outsideInbox.map(m => (
                                <li key={m.graph_id} className="border border-amber-500/30 bg-amber-500/5 rounded px-2 py-1.5">
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="font-mono text-[11px] truncate" title={m.subject}>
                                      {m.subject || '(no subject)'}
                                    </span>
                                    <span className="text-[10px] font-mono opacity-70">
                                      {formatTimestamp(m.received_at)}
                                    </span>
                                  </div>
                                  <div className="text-[11px] text-muted-foreground truncate">
                                    {m.from_address || ''} · folder:{' '}
                                    <span className="font-mono text-amber-600 dark:text-amber-300">
                                      {m.folder_name || m.parent_folder_id || '(unknown)'}
                                    </span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                            <p className="text-[10px] text-muted-foreground mt-1 italic">
                              Sync only pulls from Inbox. If your latest mail is
                              showing up in Junk / a rule-routed folder, the
                              dashboard won't see it. Move the message back to
                              Inbox or adjust your Outlook rules.
                            </p>
                          </div>
                        )
                      })()
                    )}

                    {/* Inbox subtree — the smoking gun for sub-folder routing.
                        Outlook for Business commonly puts mail into a child of
                        Inbox via a rule or manual move. The bare
                        /me/mailFolders/inbox/messages endpoint does NOT recurse
                        into children, so any sub-folder mail used to be invisible
                        to the sync. The fix walks the full tree; this panel makes
                        the tree visible so the user can see exactly where their
                        latest mail lives. */}
                    {diagnostics.data.graph_probe.inbox_tree && diagnostics.data.graph_probe.inbox_tree.length > 0 && (
                      (() => {
                        const tree = diagnostics.data!.graph_probe!.inbox_tree!
                        const treeNewest = diagnostics.data!.graph_probe!.inbox_tree_newest_received_at ?? null
                        const inboxRecent = diagnostics.data!.graph_probe!.inbox_recent ?? []
                        const topInboxNewest = inboxRecent[0]?.received_at ?? null
                        const subfolderIsAhead = treeNewest && topInboxNewest && treeNewest > topInboxNewest
                        return (
                          <div className={cn(
                            'rounded border px-2 py-2',
                            subfolderIsAhead
                              ? 'border-amber-500/40 bg-amber-500/5'
                              : 'border-border',
                          )}>
                            <p className="text-muted-foreground mb-1 flex items-center gap-1.5">
                              <Activity className="w-3 h-3" />
                              Inbox subtree ({tree.length} folder{tree.length === 1 ? '' : 's'})
                              {subfolderIsAhead && (
                                <span className="text-amber-600 dark:text-amber-300 font-medium ml-1">
                                  · sub-folder is fresher than top-level Inbox
                                </span>
                              )}
                            </p>
                            <ul className="font-mono space-y-0.5">
                              {tree
                                .slice()
                                .sort((a, b) => {
                                  const at = a.newest_received_at || ''
                                  const bt = b.newest_received_at || ''
                                  return bt.localeCompare(at)
                                })
                                .slice(0, 12)
                                .map((entry, i) => (
                                  <li key={(entry.id ?? i) + '-' + i} className="flex justify-between gap-2 px-1">
                                    <span className="truncate">
                                      {'· '.repeat(entry.depth)}
                                      {entry.name || '(unnamed)'}
                                      <span className="opacity-50 ml-1.5">
                                        [{entry.total ?? 0} / {entry.unread ?? 0} unread]
                                      </span>
                                    </span>
                                    <span className="opacity-70 whitespace-nowrap">
                                      {entry.newest_received_at
                                        ? formatTimestamp(entry.newest_received_at)
                                        : '—'}
                                    </span>
                                  </li>
                                ))}
                            </ul>
                            {subfolderIsAhead && (
                              <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1.5 italic">
                                The newest mail in your Inbox tree ({formatTimestamp(treeNewest)})
                                is in a sub-folder, not at the top of Inbox
                                ({formatTimestamp(topInboxNewest)}). The sub-folder
                                walk shipped in the latest deploy will pick this up
                                next time you click <span className="font-mono">Sync Now</span>.
                              </p>
                            )}
                          </div>
                        )
                      })()
                    )}

                    {/* Folder counts — useful "mail is going somewhere" signal */}
                    {diagnostics.data.graph_probe.folders && Object.keys(diagnostics.data.graph_probe.folders).length > 0 && (
                      <details className="text-[11px]">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                          Folder counts ({Object.keys(diagnostics.data.graph_probe.folders).length})
                        </summary>
                        <ul className="mt-1.5 grid grid-cols-2 gap-1 font-mono">
                          {Object.values(diagnostics.data.graph_probe.folders)
                            .filter(f => f.name)
                            .sort((a, b) => (b.total || 0) - (a.total || 0))
                            .slice(0, 12)
                            .map((f, i) => (
                              <li key={i} className="flex justify-between gap-2 px-1.5">
                                <span className="truncate">{f.name}</span>
                                <span className="opacity-70 whitespace-nowrap">
                                  {f.total ?? 0} ({f.unread ?? 0} unread)
                                </span>
                              </li>
                            ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        {/* Backfill engine */}
        <section className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-medium flex items-center gap-2">
                <History className="w-4 h-4" /> Historical backfill
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Silently walks your mailbox newest-first in widening windows
                ({BACKFILL_STEPS.join(' → ')} days), one chunk every few
                minutes, all the way back up to ~{Math.round(BACKFILL_MAX_DAYS / 365)}{' '}
                years - or until Outlook stops returning older mail. Resumes
                across tab refreshes; if a chunk fails it auto-retries on the
                next pass.
              </p>
            </div>
            <button
              type="button"
              onClick={handleRestartBackfill}
              className="text-xs px-2.5 py-1.5 rounded-md border border-border hover:bg-accent transition-colors flex items-center gap-1.5"
              title="Reset the cursor and re-walk from now"
            >
              <RotateCcw className="w-3 h-3" />
              Restart
            </button>
          </div>

          {/* Big progress bar */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium">
                {backfill.state.complete
                  ? 'Caught up'
                  : `Window: last ${backfill.state.oldest_days || backfill.state.oldest_days === 0 ? backfill.state.oldest_days : 0} days`}
              </span>
              <span className="font-mono">{backfill.progressPct}%</span>
            </div>
            <div className="h-2 bg-border rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-500',
                  backfill.state.complete
                    ? 'bg-emerald-500'
                    : backfill.state.last_outcome === 'error'
                      ? 'bg-amber-500'
                      : 'bg-primary',
                )}
                style={{ width: `${backfill.progressPct}%` }}
              />
            </div>
            <p className="text-[11px] text-muted-foreground">{backfill.state.last_message}</p>
          </div>

          {/* Live counters */}
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs pt-2 border-t border-border">
            <div>
              <dt className="text-muted-foreground">Oldest synced window</dt>
              <dd className="font-mono text-base">{backfill.state.oldest_days} d</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Chunks attempted</dt>
              <dd className="font-mono text-base">{backfill.state.total_chunks}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Emails backfilled</dt>
              <dd className="font-mono text-base">{backfill.state.total_created}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" /> Last run
              </dt>
              <dd className="font-mono text-base">{formatRelative(backfill.state.last_run_at || null)}</dd>
            </div>
          </dl>

          {/* Status pill */}
          <div className="flex flex-wrap items-center gap-2 text-[11px] pt-2 border-t border-border">
            <span className="text-muted-foreground">Status:</span>
            <span
              className={cn(
                'px-2 py-0.5 rounded-full font-medium',
                backfill.state.last_outcome === 'complete' && 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
                backfill.state.last_outcome === 'progress' && 'bg-primary/15 text-primary',
                backfill.state.last_outcome === 'error' && 'bg-amber-500/15 text-amber-500',
                backfill.state.last_outcome === 'pending' && 'bg-muted text-muted-foreground',
              )}
            >
              {backfill.state.last_outcome}
            </span>
            {backfill.state.consecutive_empty > 0 && !backfill.state.complete && (
              <span className="text-muted-foreground">
                {backfill.state.consecutive_empty}/{BACKFILL_COMPLETE_AFTER_EMPTY} empty steps
              </span>
            )}
          </div>
        </section>

        {/* Recent backfill events */}
        <section className="rounded-lg border border-border bg-card p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Recent backfill activity</h2>
            <button
              type="button"
              onClick={() => { void refreshEvents() }}
              disabled={eventsLoading}
              className="text-xs p-1 rounded hover:bg-accent transition-colors disabled:opacity-50"
              aria-label="Refresh events"
            >
              {eventsLoading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </button>
          </div>
          {recentBackfill.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No backfill chunks recorded yet. The first chunk fires within 30s
              of opening the email tab.
            </p>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {recentBackfill.slice(0, 12).map(ev => {
                const ctx = (ev.context ?? {}) as Record<string, unknown>
                const days = typeof ctx.days_back === 'number' ? ctx.days_back : null
                const created = typeof ctx.emails_created === 'number' ? ctx.emails_created : null
                return (
                  <li
                    key={ev.id}
                    className="flex items-start gap-2 py-1.5 border-b border-border last:border-0"
                  >
                    <span className={cn('mt-0.5 font-mono', severityClass(ev.severity))}>
                      {ev.severity === 'error' ? '✕' : ev.severity === 'warning' ? '!' : '•'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="truncate" title={ev.message}>{ev.message}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {formatTimestamp(ev.created_at)}
                        {days !== null && ` · window ${days}d`}
                        {created !== null && ` · +${created} emails`}
                        {ev.reason && ` · ${ev.reason}`}
                      </p>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {/* Admin-only: org-wide mailbox health (scope=org). Self-hides
            for non-admins - see OrgMailboxHealthPanel. This is the
            "admin dashboard" surface for OTHER users' dead mailboxes;
            the reconnect banner above the inbox covers only the
            caller's own. */}
        <OrgMailboxHealthPanel />
    </div>
  )
}

/**
 * Legacy `/email/settings` page wrapper — preserves the bookmarkable
 * route for users who still navigate via the URL bar. New entry points
 * (sidebar "Sync & Status", gear-icon Settings → "Sync & Status" tab)
 * surface the same content inside the universal Settings modal.
 */
export default function SyncSettingsPage() {
  return (
    // BUG-0037 mobile-parity (Mark Newman 2026-05-17): `p-6` was a flat
    // 24px on every edge — wasted ~48px of horizontal real estate on a
    // 360px viewport. Drop to `p-3` on narrow, `sm:p-6` from 640px up
    // so desktop density is unchanged. Section gap `space-y-6` (24px)
    // tightened to `space-y-4 sm:space-y-6` for the same reason.
    <div className="flex-1 overflow-auto bg-background">
      <div className="max-w-4xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to="/email/inbox"
            className="p-1.5 rounded-md hover:bg-accent transition-colors shrink-0"
            aria-label="Back to inbox"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold">Email sync settings</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Live status of your Outlook and Gmail connections, manual sync, and
              continuous historical backfill.
            </p>
          </div>
        </div>
        <SyncSettingsContent />
      </div>
    </div>
  )
}
